// purehealthnexus/src/components/nutrition/fitnessbodycomp/nutritionFitnessBodyComp.jsx
// Fitness & Body Composition: options, initial data, groups, overview, focus,
// statuses, special tiles, and BMI-specific insights. No UI, no Firebase here.
//
// This module is pure data + helpers. It expects the caller to pass in a
// "bmi" node built from the user's Firebase profile + NNR (UseDaily) +
// nutritionCalculate snapshots using buildBmiNodeFromSnapshots().

//
// ---- BMI constants (clinical cut-offs, kept in one place) ----
//

const BMI_NORMAL_MIN = 18.5;
const BMI_NORMAL_MAX = 24.9;
const BMI_OVERWEIGHT_THRESHOLD = 25;
const BMI_OBESITY_THRESHOLD = 30;

// Rough energy cost per kilogram of body weight (kcal/kg).
const KCAL_PER_KILOGRAM = 7700;

//
// ---- Tile registry ----
//

// Single source of truth for which fitness/body composition tiles exist.
export const fitnessBodyOptions = [{ id: "bmi", label: "BMI & Body Fat" }];

export const fitnessBodyInitialData = {
  bmi: {
    // Core BMI numbers
    value: null, // e.g. 36.4
    category: null, // e.g. "Obesity"

    // Body measures
    weightKg: null, // e.g. 115
    heightCm: null, // e.g. 177.8
    heightMeters: null, // e.g. 1.778

    // Reference data from NNR
    meanLifeStageBmi: null, // e.g. 23.09
    referenceWeightKg: null, // e.g. 74.8

    // Derived gaps (positive = "kg to lose")
    kilogramsAboveReference: null,
    kilogramsToReachBmiBelow30: null,
    kilogramsToReachNormalUpper: null,

    // Intake context (from nutritionCalculate)
    energyPercentOfTarget: null,
    fatPercentOfTarget: null,
    carbohydratesPercentOfTarget: null,
    fibrePercentOfTarget: null,
    proteinPercentOfTarget: null,

    // Energy balance (UseDaily EER vs logged intake)
    estimatedEnergyRequirementKcal: null,
    averageIntakeEnergyKcal: null,
    dailyEnergyBalanceKcal: null, // intake - requirement (negative = deficit)

    // Optional extra body-comp metrics if you have them
    bodyFat: null, // percentage (measured or estimated)
    waistToHeight: null, // ratio

    // Profile context if needed for estimations
    ageYears: null,
    sex: null,

    // Range metadata (comes from nutritionTargetsCalculations)
    rangeKind: null, // e.g. "7d", "30d"
    rangeDaysInRange: null, // e.g. 7, 30
    rangeTotalDaysLogged: null, // e.g. 6, 16

    // Explicit weight-loss goal (from user targets/settings)
    // Example source in your settings:
    //   weightLoss: true
    //   looseWeight: 10
    hasWeightLossGoal: false,
    weightLossGoalKilograms: null,

    // Micronutrient summary for this BMI tile (derived from comparisonVsTargets)
    nutrientPrioritySummaryText: null,
  },
};

//
// ---- Helpers: safe numeric formatting and PAL inference ----
//

function isFiniteNumber(value) {
  return typeof value === "number" && Number.isFinite(value);
}

function formatNumber(value, decimals = 1) {
  if (!isFiniteNumber(value)) {
    return null;
  }
  return Number(value.toFixed(decimals));
}

function formatSignedKilograms(value) {
  if (!isFiniteNumber(value)) {
    return "-";
  }
  const rounded = formatNumber(value, 1);
  if (rounded > 0) {
    return `+${rounded} kg`;
  }
  if (rounded < 0) {
    return `${rounded} kg`;
  }
  return "0 kg";
}

function formatKilogramsToLose(value) {
  if (!isFiniteNumber(value) || value <= 0) {
    return "-";
  }
  const rounded = Math.round(value);
  return `${rounded} kg to lose`;
}

/**
 * Map the user's activity node to an appropriate PAL key for EER.
 * This stays very defensive and can be refined later.
 */
function inferPalKeyFromActivityNode(activityNode, eerByPal) {
  if (!activityNode || !eerByPal || typeof eerByPal !== "object") {
    return null;
  }

  const physicalActivityLevelId = String(
    activityNode.physicalActivityLevelId || "",
  ).toLowerCase();

  if (
    physicalActivityLevelId.includes("sedentary") ||
    physicalActivityLevelId === "low"
  ) {
    return "1.4";
  }

  if (physicalActivityLevelId.includes("moderate")) {
    if (Object.prototype.hasOwnProperty.call(eerByPal, "1.6")) {
      return "1.6";
    }
    if (Object.prototype.hasOwnProperty.call(eerByPal, "1.8")) {
      return "1.8";
    }
    return null;
  }

  if (
    physicalActivityLevelId.includes("high") ||
    physicalActivityLevelId.includes("vigorous") ||
    physicalActivityLevelId.includes("very_active") ||
    physicalActivityLevelId.includes("very-active")
  ) {
    if (Object.prototype.hasOwnProperty.call(eerByPal, "1.8")) {
      return "1.8";
    }
    if (Object.prototype.hasOwnProperty.call(eerByPal, "1.6")) {
      return "1.6";
    }
    return null;
  }

  // Fallback to a reasonable mid value if present.
  if (Object.prototype.hasOwnProperty.call(eerByPal, "1.6")) {
    return "1.6";
  }

  return null;
}

//
// ---- Helper: summarise key nutrient gaps for BMI tile ----
//

const MICRONUTRIENT_ID_TO_LABEL = {
  vitaminA_µg_RE: "Vitamin A",
  vitaminD_µg: "Vitamin D",
  vitaminE_mg_aTE: "Vitamin E",
  vitaminK_µg: "Vitamin K",
  vitaminC_mg: "Vitamin C",
  thiamin_mg: "Thiamin (B1)",
  riboflavin_mg: "Riboflavin (B2)",
  niacin_NE: "Niacin",
  vitaminB6_mg: "Vitamin B6",
  folate_µg: "Folate",
  vitaminB12_µg: "Vitamin B12",
  biotin_µg: "Biotin",
  pantothenicAcid_mg: "Pantothenic acid",
  calcium_mg: "Calcium",
  iron_mg: "Iron",
  zinc_mg: "Zinc",
  magnesium_mg: "Magnesium",
  phosphorus_mg: "Phosphorus",
  potassium_mg: "Potassium",
  iodine_µg: "Iodine",
  selenium_µg: "Selenium",
  copper_µg: "Copper",
  manganese_mg: "Manganese",
  molybdenum_µg: "Molybdenum",
  fluoride_mg: "Fluoride",
  salt_g: "Salt",
};

const MACRO_LIKE_NUTRIENT_IDS = new Set([
  "energy_kcal",
  "energy_kj",
  "protein_g",
  "fat_g",
  "carbohydrates_g",
  "fibre_g",
  "saturates_g",
  "sugars_g",
  "mufa_g",
  "pufa_g",
  "ala_g",
  "n3_pufa_g",
]);

/**
 * From comparisonVsTargets, pick out the biggest nutrient gaps
 * (vitamins, minerals, etc.) so the BMI tile can talk about
 * “healthy weight-loss” rather than just calories.
 */
export function buildNutrientPrioritySummary(comparisonVsTargets) {
  if (!comparisonVsTargets || typeof comparisonVsTargets !== "object") {
    return null;
  }

  const nutrientDeficits = [];

  for (const [nutrientId, entry] of Object.entries(comparisonVsTargets)) {
    if (!entry || MACRO_LIKE_NUTRIENT_IDS.has(nutrientId)) {
      continue;
    }

    const target = entry.target;
    const percentOfTarget = entry.percentOfTarget;

    if (!isFiniteNumber(target) || !isFiniteNumber(percentOfTarget)) {
      continue;
    }

    // Only keep nutrients meaningfully below target.
    if (percentOfTarget >= 80) {
      continue;
    }

    const label = MICRONUTRIENT_ID_TO_LABEL[nutrientId] || nutrientId;

    nutrientDeficits.push({
      nutrientId,
      label,
      percentOfTarget,
    });
  }

  if (nutrientDeficits.length === 0) {
    return null;
  }

  // Worst first.
  nutrientDeficits.sort(
    (first, second) => first.percentOfTarget - second.percentOfTarget,
  );

  const worstFour = nutrientDeficits.slice(0, 4);
  const summaryParts = worstFour.map((item) => {
    const roundedPercent = Math.round(item.percentOfTarget);
    return `${item.label} ${roundedPercent}%`;
  });

  return `${summaryParts.join(
    ", ",
  )} of target (largest gaps on logged days).`;
}

//
// ---- OOP helper: BMI insight model ----
//

export class BmiInsight {
  /**
   * node is expected to look like fitnessBodyInitialData.bmi,
   * ideally created via buildBmiNodeFromSnapshots().
   */
  constructor(node) {
    this.node = node || {};
  }

  // ---- Core getters ----

  get bmiValue() {
    return isFiniteNumber(this.node.value) ? this.node.value : null;
  }

  get bmiCategory() {
    if (typeof this.node.category === "string" && this.node.category.trim()) {
      return this.node.category.trim();
    }

    const bmi = this.bmiValue;
    if (!isFiniteNumber(bmi)) {
      return null;
    }

    if (bmi < BMI_NORMAL_MIN) return "Underweight";
    if (bmi < BMI_OVERWEIGHT_THRESHOLD) return "Normal weight";
    if (bmi < BMI_OBESITY_THRESHOLD) return "Overweight";
    return "Obesity";
  }

  get isObesityRange() {
    const bmi = this.bmiValue;
    return isFiniteNumber(bmi) && bmi >= BMI_OBESITY_THRESHOLD;
  }

  get weightKg() {
    return isFiniteNumber(this.node.weightKg) ? this.node.weightKg : null;
  }

  get heightCm() {
    return isFiniteNumber(this.node.heightCm) ? this.node.heightCm : null;
  }

  get heightMeters() {
    if (isFiniteNumber(this.node.heightMeters)) {
      return this.node.heightMeters;
    }
    if (isFiniteNumber(this.node.heightCm)) {
      return this.node.heightCm / 100;
    }
    return null;
  }

  get referenceWeightKg() {
    return isFiniteNumber(this.node.referenceWeightKg)
      ? this.node.referenceWeightKg
      : null;
  }

  get kilogramsAboveReference() {
    if (isFiniteNumber(this.node.kilogramsAboveReference)) {
      return this.node.kilogramsAboveReference;
    }

    if (this.weightKg == null || this.referenceWeightKg == null) {
      return null;
    }

    return this.weightKg - this.referenceWeightKg;
  }

  get kilogramsToReachBmiBelow30() {
    if (isFiniteNumber(this.node.kilogramsToReachBmiBelow30)) {
      return this.node.kilogramsToReachBmiBelow30;
    }

    if (!this.heightMeters || this.weightKg == null) {
      return null;
    }

    const targetWeight =
      BMI_OBESITY_THRESHOLD * this.heightMeters * this.heightMeters;
    const kilogramsToLose = this.weightKg - targetWeight;
    return kilogramsToLose > 0 ? kilogramsToLose : 0;
  }

  get kilogramsToReachNormalUpper() {
    if (isFiniteNumber(this.node.kilogramsToReachNormalUpper)) {
      return this.node.kilogramsToReachNormalUpper;
    }

    if (!this.heightMeters || this.weightKg == null) {
      return null;
    }

    const targetWeight =
      BMI_NORMAL_MAX * this.heightMeters * this.heightMeters;
    const kilogramsToLose = this.weightKg - targetWeight;
    return kilogramsToLose > 0 ? kilogramsToLose : 0;
  }

  get energyPercentOfTarget() {
    return isFiniteNumber(this.node.energyPercentOfTarget)
      ? this.node.energyPercentOfTarget
      : null;
  }

  get fatPercentOfTarget() {
    return isFiniteNumber(this.node.fatPercentOfTarget)
      ? this.node.fatPercentOfTarget
      : null;
  }

  get carbohydratesPercentOfTarget() {
    return isFiniteNumber(this.node.carbohydratesPercentOfTarget)
      ? this.node.carbohydratesPercentOfTarget
      : null;
  }

  get fibrePercentOfTarget() {
    return isFiniteNumber(this.node.fibrePercentOfTarget)
      ? this.node.fibrePercentOfTarget
      : null;
  }

  get proteinPercentOfTarget() {
    return isFiniteNumber(this.node.proteinPercentOfTarget)
      ? this.node.proteinPercentOfTarget
      : null;
  }

  get estimatedEnergyRequirementKcal() {
    return isFiniteNumber(this.node.estimatedEnergyRequirementKcal)
      ? this.node.estimatedEnergyRequirementKcal
      : null;
  }

  get averageIntakeEnergyKcal() {
    return isFiniteNumber(this.node.averageIntakeEnergyKcal)
      ? this.node.averageIntakeEnergyKcal
      : null;
  }

  /**
   * Energy balance in kcal/day (intake - requirement).
   * Negative = deficit, positive = surplus.
   */
  get dailyEnergyBalanceKcal() {
    if (isFiniteNumber(this.node.dailyEnergyBalanceKcal)) {
      return this.node.dailyEnergyBalanceKcal;
    }

    if (
      !isFiniteNumber(this.averageIntakeEnergyKcal) ||
      !isFiniteNumber(this.estimatedEnergyRequirementKcal)
    ) {
      return null;
    }

    return this.averageIntakeEnergyKcal - this.estimatedEnergyRequirementKcal;
  }

  get ageYears() {
    return isFiniteNumber(this.node.ageYears) ? this.node.ageYears : null;
  }

  get sexCodeForDeurenberg() {
    if (typeof this.node.sex === "string") {
      const normalised = this.node.sex.trim().toLowerCase();
      if (normalised === "male") return 1;
      if (normalised === "female") return 0;
    }
    return null;
  }

  // ---- Weight-loss goal metadata ----

  get hasWeightLossGoal() {
    const goal = this.weightLossGoalKilograms;
    return (
      Boolean(this.node.hasWeightLossGoal) &&
      isFiniteNumber(goal) &&
      goal > 0
    );
  }

  get weightLossGoalKilograms() {
    const raw = this.node.weightLossGoalKilograms;
    if (!isFiniteNumber(raw) || raw <= 0) {
      return null;
    }
    return raw;
  }

  // ---- Range metadata helpers ----

  get rangeKind() {
    if (typeof this.node.rangeKind === "string" && this.node.rangeKind.trim()) {
      return this.node.rangeKind.trim();
    }
    return null;
  }

  get rangeDaysInRange() {
    return isFiniteNumber(this.node.rangeDaysInRange)
      ? this.node.rangeDaysInRange
      : null;
  }

  get rangeTotalDaysLogged() {
    return isFiniteNumber(this.node.rangeTotalDaysLogged)
      ? this.node.rangeTotalDaysLogged
      : null;
  }

  /**
   * Short suffix for "average over" text.
   * e.g. "7-day range avg", "30-day range avg", "range avg"
   */
  buildRangeAverageSuffix() {
    const daysInRange = this.rangeDaysInRange;
    const kind = this.rangeKind;

    if (kind === "7d" || daysInRange === 7) {
      return "7-day range avg";
    }
    if (kind === "30d" || daysInRange === 30) {
      return "30-day range avg";
    }
    if (kind === "90d" || daysInRange === 90) {
      return "90-day range avg";
    }
    if (isFiniteNumber(daysInRange)) {
      return `${daysInRange}-day range avg`;
    }
    return "range avg";
  }

  /**
   * Label for the macros key in the BMI tile details.
   * e.g. "Avg intake (7-day range)" or "Avg intake (selected range)".
   */
  buildAverageIntakeLabel() {
    const daysInRange = this.rangeDaysInRange;
    const kind = this.rangeKind;

    if (kind === "7d" || daysInRange === 7) {
      return "Avg intake (7-day range)";
    }
    if (kind === "30d" || daysInRange === 30) {
      return "Avg intake (30-day range)";
    }
    if (kind === "90d" || daysInRange === 90) {
      return "Avg intake (90-day range)";
    }
    if (isFiniteNumber(daysInRange)) {
      return `Avg intake (${daysInRange}-day range)`;
    }
    return "Avg intake (selected range)";
  }

  /**
   * Optional hint encouraging the user to look at a longer window
   * when the active range is shorter than 30 days.
   */
  buildRangeStabilityHint() {
    const daysInRange = this.rangeDaysInRange;

    if (!isFiniteNumber(daysInRange)) {
      return null;
    }

    if (daysInRange < 30) {
      return " For more stable BMI and intake trends, consider viewing a 30-day range or longer.";
    }

    return null;
  }

  /**
   * Deurenberg formula for estimating body fat percentage:
   * bodyFat% = 1.2 * BMI + 0.23 * age - 10.8 * sex - 5.4
   * where sex = 1 for men, 0 for women.
   */
  get estimatedBodyFatPercent() {
    const bmi = this.bmiValue;
    const ageYears = this.ageYears;
    const sexCode = this.sexCodeForDeurenberg;

    if (!isFiniteNumber(bmi) || !isFiniteNumber(ageYears) || sexCode == null) {
      return null;
    }

    const raw = 1.2 * bmi + 0.23 * ageYears - 10.8 * sexCode - 5.4;

    if (!isFiniteNumber(raw)) {
      return null;
    }

    return raw;
  }

  /**
   * Projected weekly weight change in kilograms if the current
   * daily energy balance were maintained.
   * Negative = weight loss, positive = weight gain.
   */
  get projectedWeeklyWeightChangeKg() {
    const dailyBalance = this.dailyEnergyBalanceKcal;
    if (!isFiniteNumber(dailyBalance) || dailyBalance === 0) {
      return null;
    }

    const weeklyBalance = dailyBalance * 7;
    const weightChange = weeklyBalance / KCAL_PER_KILOGRAM;

    if (!isFiniteNumber(weightChange)) {
      return null;
    }

    return weightChange;
  }

  get nutrientPrioritySummaryText() {
    if (
      typeof this.node.nutrientPrioritySummaryText === "string" &&
      this.node.nutrientPrioritySummaryText.trim()
    ) {
      return this.node.nutrientPrioritySummaryText.trim();
    }
    return null;
  }

  // ---- Weight-loss plan builder ----

  /**
   * Compute a structured weight-loss plan using the user's explicit goal,
   * current weight and estimated energy requirement.
   *
   * Returns null if no valid goal is present.
   */
  computeWeightLossPlan() {
    if (!this.hasWeightLossGoal || !isFiniteNumber(this.weightKg)) {
      return null;
    }

    const goalKilograms = this.weightLossGoalKilograms;
    if (!isFiniteNumber(goalKilograms) || goalKilograms <= 0) {
      return null;
    }

    const currentWeightKilograms = this.weightKg;

    const targetWeightRaw = currentWeightKilograms - goalKilograms;
    const targetWeightKilograms =
      isFiniteNumber(targetWeightRaw) && targetWeightRaw > 0
        ? targetWeightRaw
        : null;

    // Safe weekly pace range in kg/week.
    const safeWeeklyRateKilogramsMin = 0.25;
    const safeWeeklyRateKilogramsMax = 1.0;
    const preferredWeeklyRateKilograms = 0.5;

    const indicativeWeeksRangeMin = Math.ceil(
      goalKilograms / safeWeeklyRateKilogramsMax,
    );
    const indicativeWeeksRangeMax = Math.ceil(
      goalKilograms / safeWeeklyRateKilogramsMin,
    );
    const indicativeWeeksPreferred = Math.ceil(
      goalKilograms / preferredWeeklyRateKilograms,
    );

    // Theoretical daily deficit for the preferred weekly rate.
    let theoreticalPreferredDeficitKcal = null;
    if (preferredWeeklyRateKilograms > 0) {
      theoreticalPreferredDeficitKcal = Math.round(
        (preferredWeeklyRateKilograms * KCAL_PER_KILOGRAM) / 7,
      );
    }

    let recommendationDailyDeficitKilocalories =
      theoreticalPreferredDeficitKcal;
    let recommendationTargetIntakeKilocalories = null;

    const estimatedRequirement = this.estimatedEnergyRequirementKcal;
    if (
      isFiniteNumber(estimatedRequirement) &&
      isFiniteNumber(recommendationDailyDeficitKilocalories)
    ) {
      // Simple minimum intake floors by sex to avoid extreme values.
      const sexCode = this.sexCodeForDeurenberg;
      const minimumIntakeFloor =
        sexCode === 1
          ? 1500 // typical minimum for many adult men
          : sexCode === 0
            ? 1200 // typical minimum for many adult women
            : 1200;

      const naiveIntake =
        estimatedRequirement - recommendationDailyDeficitKilocalories;

      // Clamp intake to a safe floor if needed.
      recommendationTargetIntakeKilocalories =
        naiveIntake < minimumIntakeFloor ? minimumIntakeFloor : naiveIntake;

      // Recompute the effective deficit if we hit the floor.
      recommendationDailyDeficitKilocalories =
        estimatedRequirement - recommendationTargetIntakeKilocalories;
    }

    // Back-compute the implied weekly rate from the recommended deficit.
    let impliedWeeklyRateKilograms = null;
    if (
      isFiniteNumber(recommendationDailyDeficitKilocalories) &&
      recommendationDailyDeficitKilocalories > 0
    ) {
      impliedWeeklyRateKilograms =
        (recommendationDailyDeficitKilocalories * 7) / KCAL_PER_KILOGRAM;
    }

    let indicativeWeeksFromRecommended = null;
    if (
      isFiniteNumber(impliedWeeklyRateKilograms) &&
      impliedWeeklyRateKilograms > 0
    ) {
      indicativeWeeksFromRecommended = Math.ceil(
        goalKilograms / impliedWeeklyRateKilograms,
      );
    }

    return {
      hasWeightLossGoal: true,
      goalKilograms,
      currentWeightKilograms,
      targetWeightKilograms,
      safeWeeklyRateKilogramsMin,
      safeWeeklyRateKilogramsMax,
      preferredWeeklyRateKilograms,
      recommendationDailyDeficitKilocalories: isFiniteNumber(
        recommendationDailyDeficitKilocalories,
      )
        ? Math.round(recommendationDailyDeficitKilocalories)
        : null,
      recommendationTargetIntakeKilocalories: isFiniteNumber(
        recommendationTargetIntakeKilocalories,
      )
        ? Math.round(recommendationTargetIntakeKilocalories)
        : null,
      indicativeWeeksRangeMin,
      indicativeWeeksRangeMax,
      indicativeWeeksPreferred,
      indicativeWeeksFromRecommended,
    };
  }

  /**
   * Build plan rows for the BMI tile details when a weight-loss goal is set.
   * This becomes the detailed “plan” section in the tile.
   */
  buildWeightLossPlanRows() {
    const plan = this.computeWeightLossPlan();
    if (!plan) {
      return [];
    }

    const rows = [];

    // Goal and target weight.
    const goalText = formatNumber(plan.goalKilograms, 1);
    const targetWeightText = isFiniteNumber(plan.targetWeightKilograms)
      ? ` to ~${formatNumber(plan.targetWeightKilograms, 1)} kg`
      : "";
    rows.push({
      key: "weightLossGoal",
      label: "Weight-loss goal",
      value: `Lose ~${goalText} kg${targetWeightText}`,
    });

    // Indicative timeline based on the recommended deficit, if possible.
    if (isFiniteNumber(plan.indicativeWeeksFromRecommended)) {
      rows.push({
        key: "weightLossTimeline",
        label: "Indicative timeline",
        value:
          `About ${plan.indicativeWeeksFromRecommended} weeks at ~${plan.preferredWeeklyRateKilograms.toFixed(
            1,
          )} kg/week if you follow this plan ` +
          `(typical safe range is ${plan.indicativeWeeksRangeMin}–${plan.indicativeWeeksRangeMax} weeks).`,
      });
    } else if (
      isFiniteNumber(plan.indicativeWeeksRangeMin) &&
      isFiniteNumber(plan.indicativeWeeksRangeMax)
    ) {
      rows.push({
        key: "weightLossTimeline",
        label: "Indicative timeline",
        value: `Roughly ${plan.indicativeWeeksRangeMin}–${plan.indicativeWeeksRangeMax} weeks at about ${plan.safeWeeklyRateKilogramsMin.toFixed(
          1,
        )}–${plan.safeWeeklyRateKilogramsMax.toFixed(
          1,
        )} kg/week (approximate).`,
      });
    }

    // Indicative energy plan if we can compute it.
    if (
      isFiniteNumber(plan.recommendationTargetIntakeKilocalories) &&
      isFiniteNumber(plan.recommendationDailyDeficitKilocalories)
    ) {
      rows.push({
        key: "weightLossEnergyPlan",
        label: "Indicative energy plan",
        value:
          `Approx. ${plan.recommendationTargetIntakeKilocalories} kcal/day, ` +
          `about ${plan.recommendationDailyDeficitKilocalories} kcal/day below your estimated needs (a typical weight-loss range is around 400–800 kcal/day).`,
      });
    } else if (isFiniteNumber(plan.recommendationDailyDeficitKilocalories)) {
      rows.push({
        key: "weightLossEnergyPlan",
        label: "Indicative energy plan",
        value: `Aim for an average deficit of around ${plan.recommendationDailyDeficitKilocalories} kcal/day.`,
      });
    }

    return rows;
  }

  /**
   * SMART-style summary: turns the goal into a short, concrete plan.
   */
  buildSmartPlanSummary() {
    const plan = this.computeWeightLossPlan();
    if (!plan) {
      return null;
    }

    const weeks =
      plan.indicativeWeeksFromRecommended ||
      plan.indicativeWeeksPreferred ||
      plan.indicativeWeeksRangeMin;

    const durationText = isFiniteNumber(weeks)
      ? `over about ${weeks} weeks`
      : "over the coming weeks";

    const targetIntakeText = isFiniteNumber(
      plan.recommendationTargetIntakeKilocalories,
    )
      ? `${plan.recommendationTargetIntakeKilocalories} kcal/day`
      : "a moderate daily energy deficit";

    const deficitText = isFiniteNumber(
      plan.recommendationDailyDeficitKilocalories,
    )
      ? `~${plan.recommendationDailyDeficitKilocalories} kcal below your estimated needs`
      : "a moderate deficit vs your estimated needs";

    const proteinPercent = this.proteinPercentOfTarget;
    const fibrePercent = this.fibrePercentOfTarget;

    const proteinAndFibrePart =
      isFiniteNumber(proteinPercent) || isFiniteNumber(fibrePercent)
        ? `keep protein and fibre close to 100% of target on most days`
        : `keep protein- and fibre-rich foods present at each meal`;

    const nutrientSummary = this.nutrientPrioritySummaryText;

    const nutrientPart = nutrientSummary
      ? ` and steadily close gaps in key nutrients (${nutrientSummary}).`
      : ".";

    return (
      `Aim for ${targetIntakeText} (${deficitText}) ${durationText}, ` +
      `${proteinAndFibrePart}${nutrientPart} Try to log what you eat on every single intake so the numbers stay accurate.`
    );
  }

  /**
   * Short helper string for the tile.
   * Uses the selected range (7d, 30d, etc.) and shows deficit or key goal posts.
   * The 30-day-stability hint is appended when the active window is short.
   */
  buildHelperText() {
    const bmi = this.bmiValue;
    const category = this.bmiCategory;

    if (!isFiniteNumber(bmi)) {
      const base =
        "Healthy BMI is typically 18.5–24.9, but personal targets vary.";
      const stabilityHint = this.buildRangeStabilityHint();
      return stabilityHint ? `${base}${stabilityHint}` : base;
    }

    const bmiFormatted = formatNumber(bmi, 1);
    const prefix = category
      ? `BMI ${bmiFormatted} (${category}).`
      : `BMI ${bmiFormatted}.`;

    const dailyBalance = this.dailyEnergyBalanceKcal;
    const daysInRange = this.rangeDaysInRange;
    const totalLoggedDays = this.rangeTotalDaysLogged;

    let mainMessage = prefix;

    if (this.isObesityRange && isFiniteNumber(dailyBalance)) {
      const magnitude = Math.abs(dailyBalance);
      const roundedMagnitude = Math.round(magnitude / 50) * 50;
      const hasStrongDeficit = dailyBalance <= -300;
      const hasVeryLargeDeficit = dailyBalance <= -1000;

      let coverageNote = "";
      if (
        isFiniteNumber(daysInRange) &&
        isFiniteNumber(totalLoggedDays) &&
        daysInRange > 0
      ) {
        const coveragePercent = Math.round(
          (totalLoggedDays / daysInRange) * 100,
        );
        coverageNote = ` based on ${totalLoggedDays} of ${daysInRange} days logged (~${coveragePercent}% of days).`;
      }

      if (hasVeryLargeDeficit) {
        mainMessage =
          `${prefix} Large logged deficit: your logged intake is about ${roundedMagnitude} kcal below your estimated daily needs (${this.buildRangeAverageSuffix()})` +
          `${coverageNote} This is higher than the 400–800 kcal/day deficit that is often used in gradual weight-loss plans and may reflect under-logging or a very aggressive pattern.`;
      } else if (hasStrongDeficit) {
        mainMessage =
          `${prefix} Your logged intake is about ${roundedMagnitude} kcal below your estimated daily needs (${this.buildRangeAverageSuffix()})` +
          `${coverageNote} This sits within a typical range for intentional weight loss if the pattern is sustainable for you.`;
      } else if (dailyBalance < 0) {
        mainMessage =
          `${prefix} Slight logged deficit (${roundedMagnitude} kcal/day below estimated needs ${this.buildRangeAverageSuffix()})` +
          `${coverageNote} which may support gradual weight loss over time.`;
      } else {
        mainMessage =
          `${prefix} Your logged intake is close to or above your estimated needs on average (${this.buildRangeAverageSuffix()})` +
          `${coverageNote}`;
      }
    } else {
      // If no strong deficit pattern, fall back to goal-post messaging.
      const kilogramsToBmi30 = this.kilogramsToReachBmiBelow30;
      const kilogramsToNormal = this.kilogramsToReachNormalUpper;

      if (
        this.isObesityRange &&
        isFiniteNumber(kilogramsToBmi30) &&
        kilogramsToBmi30 > 0
      ) {
        mainMessage = `${prefix} Around ${Math.round(
          kilogramsToBmi30,
        )} kg weight loss would bring you below BMI 30.`;
      } else if (
        !this.isObesityRange &&
        isFiniteNumber(kilogramsToNormal) &&
        kilogramsToNormal > 0
      ) {
        mainMessage = `${prefix} About ${Math.round(
          kilogramsToNormal,
        )} kg weight loss would bring you into the upper end of the healthy range.`;
      } else {
        mainMessage = prefix;
      }
    }

    const stabilityHint = this.buildRangeStabilityHint();
    return stabilityHint ? `${mainMessage}${stabilityHint}` : mainMessage;
  }

  /**
   * Compact macro summary string.
   * "Energy 65%, Protein 90%, Fat 113%, Carbs 12%, Fibre 29% of target"
   * (the "average over" qualifier is provided by the label / suffix helpers).
   */
  buildMacroSummary() {
    const pieces = [];

    if (this.energyPercentOfTarget != null) {
      pieces.push(`Energy ${Math.round(this.energyPercentOfTarget)}%`);
    }
    if (this.proteinPercentOfTarget != null) {
      pieces.push(`Protein ${Math.round(this.proteinPercentOfTarget)}%`);
    }
    if (this.fatPercentOfTarget != null) {
      pieces.push(`Fat ${Math.round(this.fatPercentOfTarget)}%`);
    }
    if (this.carbohydratesPercentOfTarget != null) {
      pieces.push(`Carbs ${Math.round(this.carbohydratesPercentOfTarget)}%`);
    }
    if (this.fibrePercentOfTarget != null) {
      pieces.push(`Fibre ${Math.round(this.fibrePercentOfTarget)}%`);
    }

    if (pieces.length === 0) {
      return null;
    }

    return `${pieces.join(", ")} of target`;
  }

  /**
   * High-level "task" for the tile.
   * If the user has a weight-loss goal, base this on that;
   * otherwise keep the tile informational-only (no prescriptive focus).
   */
  buildTaskSummary() {
    if (!isFiniteNumber(this.weightKg)) {
      return null;
    }

    // Only surface a task when the user has explicitly chosen a weight-loss goal.
    if (this.hasWeightLossGoal && isFiniteNumber(this.weightLossGoalKilograms)) {
      const goalKilograms = this.weightLossGoalKilograms;
      if (goalKilograms > 0) {
        const targetWeightKilograms =
          this.weightKg - goalKilograms > 0
            ? this.weightKg - goalKilograms
            : null;
        const targetWeightText = isFiniteNumber(targetWeightKilograms)
          ? ` (to about ${formatNumber(targetWeightKilograms, 1)} kg)`
          : "";
        return `Focus on losing about ${Math.round(
          goalKilograms,
        )} kg${targetWeightText} in sustainable steps.`;
      }
    }

    // No explicit goal: show BMI details but no "Suggested focus" weight-loss prompt.
    return null;
  }

  /**
   * Build key–value rows shown in the tile details section.
   * This is the "deep dive" view, including the optional weight-loss plan.
   */
  buildKeyValueRows() {
    const rows = [];

    // Current position.
    if (isFiniteNumber(this.weightKg)) {
      rows.push({
        key: "weight",
        label: "Weight",
        value: `${formatNumber(this.weightKg, 1)} kg`,
      });
    }

    if (this.bmiCategory) {
      rows.push({
        key: "bmiCategory",
        label: "BMI category",
        value: this.bmiCategory,
      });
    }

    // Estimated body fat if no measured body fat is present.
    if (isFiniteNumber(this.node.bodyFat)) {
      rows.push({
        key: "bodyFat",
        label: "Body fat",
        value: `${formatNumber(this.node.bodyFat, 1)}%`,
      });
    } else if (typeof this.node.bodyFat === "string") {
      rows.push({
        key: "bodyFat",
        label: "Body fat",
        value: this.node.bodyFat,
      });
    } else {
      const estimatedBodyFat = this.estimatedBodyFatPercent;
      if (isFiniteNumber(estimatedBodyFat)) {
        rows.push({
          key: "estimatedBodyFat",
          label: "Estimated body fat",
          value: `${formatNumber(estimatedBodyFat, 1)}%`,
        });
      }
    }

    // How far from reference weight (NNR life-stage).
    const kilogramsAboveReference = this.kilogramsAboveReference;
    if (
      isFiniteNumber(kilogramsAboveReference) &&
      isFiniteNumber(this.referenceWeightKg)
    ) {
      rows.push({
        key: "vsReference",
        label: "Vs NNR ref.",
        value: `${formatSignedKilograms(
          kilogramsAboveReference,
        )} vs ${formatNumber(this.referenceWeightKg, 1)} kg`,
      });
    }

    // Distance to key BMI goal posts.
    const kilogramsToBmi30 = this.kilogramsToReachBmiBelow30;
    if (isFiniteNumber(kilogramsToBmi30) && kilogramsToBmi30 > 0) {
      rows.push({
        key: "toBmi30",
        label: "To BMI 30",
        value: formatKilogramsToLose(kilogramsToBmi30),
      });
    }

    const kilogramsToNormal = this.kilogramsToReachNormalUpper;
    if (isFiniteNumber(kilogramsToNormal) && kilogramsToNormal > 0) {
      rows.push({
        key: "toNormal",
        label: "To BMI 25",
        value: formatKilogramsToLose(kilogramsToNormal),
      });
    }

    // Energy need vs logged intake vs balance (transparent link to the 1600 number).
    const estimatedNeeds = this.estimatedEnergyRequirementKcal;
    const averageIntake = this.averageIntakeEnergyKcal;

    if (isFiniteNumber(estimatedNeeds)) {
      rows.push({
        key: "estimatedNeeds",
        label: "Estimated daily need",
        value: `${Math.round(
          estimatedNeeds,
        )} kcal/day (based on your profile and activity pattern).`,
      });
    }

    if (isFiniteNumber(averageIntake)) {
      rows.push({
        key: "averageIntakeEnergy",
        label: "Average logged intake",
        value: `${Math.round(
          averageIntake,
        )} kcal/day (${this.buildRangeAverageSuffix()}, logged days only).`,
      });
    }

    const dailyBalance = this.dailyEnergyBalanceKcal;
    if (isFiniteNumber(dailyBalance) && Math.abs(dailyBalance) >= 50) {
      const balanceMagnitude = Math.abs(dailyBalance);
      const roundedToNearestFifty = Math.round(balanceMagnitude / 50) * 50;
      const balanceDirection = dailyBalance < 0 ? "deficit" : "surplus";

      const daysInRange = this.rangeDaysInRange;
      const totalLoggedDays = this.rangeTotalDaysLogged;

      let coverageSuffix = "";
      if (
        isFiniteNumber(daysInRange) &&
        isFiniteNumber(totalLoggedDays) &&
        daysInRange > 0
      ) {
        const coveragePercent = Math.round(
          (totalLoggedDays / daysInRange) * 100,
        );
        coverageSuffix = `; based on ${totalLoggedDays} of ${daysInRange} days logged (~${coveragePercent}% of days).`;
      }

      rows.push({
        key: "energyBalance",
        label: "Energy balance",
        value:
          `Approximately ${roundedToNearestFifty} kcal/day ${balanceDirection} (${this.buildRangeAverageSuffix()})` +
          coverageSuffix,
      });
    }

    const projectedWeeklyChange = this.projectedWeeklyWeightChangeKg;
    if (
      isFiniteNumber(projectedWeeklyChange) &&
      Math.abs(projectedWeeklyChange) >= 0.1
    ) {
      const magnitude = Math.abs(projectedWeeklyChange);
      const magnitudeRounded = formatNumber(magnitude, 1);
      const direction = projectedWeeklyChange < 0 ? "loss" : "gain";

      rows.push({
        key: "projectedTrend",
        label: "Projected trend",
        value:
          `If this pattern continued, this would correspond to roughly ` +
          `${magnitudeRounded} kg/week ${direction} (theoretical estimate based on logged days only).`,
      });
    }

    // Macro context (energy + protein + fat + carbs + fibre vs targets).
    const macroSummary = this.buildMacroSummary();
    if (macroSummary) {
      rows.push({
        key: "macros",
        label: this.buildAverageIntakeLabel(),
        value: macroSummary,
      });
    }

    // Micronutrient priorities (vitamins, minerals etc.) for healthy weight loss.
    const nutrientPrioritySummary = this.nutrientPrioritySummaryText;
    if (nutrientPrioritySummary) {
      rows.push({
        key: "nutrientPriorities",
        label: "Nutrient priorities in deficit",
        value: nutrientPrioritySummary,
      });
    }

    // Weight-loss plan (only if a goal is set).
    const weightLossPlanRows = this.buildWeightLossPlanRows();
    for (const planRow of weightLossPlanRows) {
      rows.push(planRow);
    }

    // SMART plan summary (only when there is a goal/plan).
    const smartPlanSummary = this.buildSmartPlanSummary();
    if (smartPlanSummary) {
      rows.push({
        key: "smartPlan",
        label: "SMART plan",
        value: smartPlanSummary,
      });
    }

    // Suggested focus task (only when a weight-loss goal is active).
    const taskSummary = this.buildTaskSummary();
    if (taskSummary) {
      rows.push({
        key: "task",
        label: "Suggested focus",
        value: taskSummary,
      });
    }

    // Optional waist:height ratio if available.
    if (this.node.waistToHeight != null) {
      rows.push({
        key: "waistHeight",
        label: "Waist:height",
        value: String(this.node.waistToHeight),
      });
    }

    if (rows.length === 0) {
      rows.push({
        key: "placeholder",
        label: "Details",
        value:
          "Body composition details will appear here once data is available.",
      });
    }

    return rows;
  }
}

export function buildBmiNodeFromSnapshots({
  userTargetsSettingsSnapshot,
  useDailySnapshot,
  nutritionCalculateSnapshot,
} = {}) {
  const userBodyNode =
    userTargetsSettingsSnapshot && userTargetsSettingsSnapshot.body
      ? userTargetsSettingsSnapshot.body
      : null;

  const profileNode =
    userTargetsSettingsSnapshot && userTargetsSettingsSnapshot.profile
      ? userTargetsSettingsSnapshot.profile
      : null;

  const activityNode =
    userTargetsSettingsSnapshot && userTargetsSettingsSnapshot.activity
      ? userTargetsSettingsSnapshot.activity
      : null;

  // ---- Weight-loss goal extraction (supports new + legacy shapes) ----
  const weightLossGoalNode =
    userTargetsSettingsSnapshot && userTargetsSettingsSnapshot.weightLossGoal
      ? userTargetsSettingsSnapshot.weightLossGoal
      : null;

  const rawProfileNode =
    userTargetsSettingsSnapshot && userTargetsSettingsSnapshot.rawProfile
      ? userTargetsSettingsSnapshot.rawProfile
      : null;

  // Prefer the new weightLossGoal.isActive; fall back to legacy flags if present.
  const hasWeightLossGoal = Boolean(
    (weightLossGoalNode && weightLossGoalNode.isActive === true) ||
    userTargetsSettingsSnapshot?.weightLoss === true ||
    (rawProfileNode && rawProfileNode.weightLoss === true),
  );

  // Helper to coerce a possible number/string into a numeric kg value.
  const coerceGoalKilograms = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  let weightLossGoalKilogramsRaw = null;

  // 1) New canonical location: weightLossGoal.kilogramsToLose
  if (weightLossGoalNode && weightLossGoalNode.kilogramsToLose != null) {
    const candidate = coerceGoalKilograms(weightLossGoalNode.kilogramsToLose);
    if (candidate !== null) {
      weightLossGoalKilogramsRaw = candidate;
    }
  }

  // 2) Backward-compat: top-level looseWeight on snapshot
  if (weightLossGoalKilogramsRaw == null) {
    const looseFromSnapshot = userTargetsSettingsSnapshot
      ? userTargetsSettingsSnapshot.looseWeight
      : null;
    const candidate = coerceGoalKilograms(looseFromSnapshot);
    if (candidate !== null) {
      weightLossGoalKilogramsRaw = candidate;
    }
  }

  // 3) Backward-compat: looseWeight on rawProfile
  if (weightLossGoalKilogramsRaw == null && rawProfileNode) {
    const looseFromProfile = rawProfileNode.looseWeight;
    const candidate = coerceGoalKilograms(looseFromProfile);
    if (candidate !== null) {
      weightLossGoalKilogramsRaw = candidate;
    }
  }

  // ---- Core anthropometrics ----
  const heightCentimeters = isFiniteNumber(userBodyNode?.height?.centimeters)
    ? userBodyNode.height.centimeters
    : null;

  const heightMetersFromSnapshot = isFiniteNumber(userBodyNode?.height?.meters)
    ? userBodyNode.height.meters
    : null;

  const heightMeters =
    heightMetersFromSnapshot != null
      ? heightMetersFromSnapshot
      : heightCentimeters != null
        ? heightCentimeters / 100
        : null;

  const weightKilograms = isFiniteNumber(userBodyNode?.weight?.kilograms)
    ? userBodyNode.weight.kilograms
    : null;

  const bmiValueFromSnapshot = isFiniteNumber(
    userBodyNode?.bodyMassIndex?.value,
  )
    ? userBodyNode.bodyMassIndex.value
    : null;

  const bmiClassificationFromSnapshot =
    userBodyNode?.bodyMassIndex?.classification || null;

  // ---- NNR / life stage context ----
  const lifeStageNode =
    useDailySnapshot && useDailySnapshot.lifeStage
      ? useDailySnapshot.lifeStage
      : null;

  const lifeStageBmiNode = lifeStageNode?.bmi || null;
  const meanLifeStageBmi = isFiniteNumber(lifeStageBmiNode?.mean)
    ? lifeStageBmiNode.mean
    : null;

  const referenceWeightKg = isFiniteNumber(
    lifeStageBmiNode?.reference?.weight_kg,
  )
    ? lifeStageBmiNode.reference.weight_kg
    : null;

  const energyNode = lifeStageNode?.energy || null;
  const eerByPal =
    energyNode && typeof energyNode.EER_MJd_byPAL === "object"
      ? energyNode.EER_MJd_byPAL
      : null;

  // ---- Nutrition Calculate bridge ----
  const intakeSummary = nutritionCalculateSnapshot?.intakeSummary || null;
  const comparisonVsTargets = intakeSummary?.comparisonVsTargets || {};
  const averageDailyIntake = intakeSummary?.averageDailyIntake || {};

  const rangeInfo = nutritionCalculateSnapshot?.rangeInfo || null;
  const rangeKind = rangeInfo?.kind || null;
  const rangeDaysInRange = isFiniteNumber(rangeInfo?.daysInRange)
    ? rangeInfo.daysInRange
    : null;
  const rangeTotalDaysLogged = isFiniteNumber(
    intakeSummary?.totalDaysLogged,
  )
    ? intakeSummary.totalDaysLogged
    : null;

  const energyPercentOfTarget =
    comparisonVsTargets.energy_kcal?.percentOfTarget ?? null;
  const fatPercentOfTarget =
    comparisonVsTargets.fat_g?.percentOfTarget ?? null;
  const carbohydratesPercentOfTarget =
    comparisonVsTargets.carbohydrates_g?.percentOfTarget ?? null;
  const fibrePercentOfTarget =
    comparisonVsTargets.fibre_g?.percentOfTarget ?? null;
  const proteinPercentOfTarget =
    comparisonVsTargets.protein_g?.percentOfTarget ?? null;

  const averageIntakeEnergyKcalRaw = isFiniteNumber(
    averageDailyIntake.energy_kcal,
  )
    ? averageDailyIntake.energy_kcal
    : null;

  // Estimate EER in kcal/day using PAL mapping.
  let estimatedEnergyRequirementKcalRaw = null;
  if (eerByPal) {
    const palKey = inferPalKeyFromActivityNode(activityNode, eerByPal);
    if (palKey && isFiniteNumber(eerByPal[palKey])) {
      const eerMjPerDay = eerByPal[palKey];
      // Convert MJ → kcal (approx 239 kcal per MJ).
      estimatedEnergyRequirementKcalRaw = eerMjPerDay * 239;
    }
  }

  // Compute energy balance intake - requirement.
  let dailyEnergyBalanceKcalRaw = null;
  if (
    isFiniteNumber(averageIntakeEnergyKcalRaw) &&
    isFiniteNumber(estimatedEnergyRequirementKcalRaw)
  ) {
    dailyEnergyBalanceKcalRaw =
      averageIntakeEnergyKcalRaw - estimatedEnergyRequirementKcalRaw;
  }

  // ---- BMI + deltas ----
  let bmiValue = bmiValueFromSnapshot;
  if (
    !isFiniteNumber(bmiValue) &&
    isFiniteNumber(weightKilograms) &&
    heightMeters
  ) {
    const rawBmi = weightKilograms / (heightMeters * heightMeters);
    bmiValue = formatNumber(rawBmi, 1);
  }

  const kilogramsAboveReferenceRaw =
    isFiniteNumber(weightKilograms) && isFiniteNumber(referenceWeightKg)
      ? weightKilograms - referenceWeightKg
      : null;

  let kilogramsToReachBmiBelow30Raw = null;
  let kilogramsToReachNormalUpperRaw = null;

  if (heightMeters && isFiniteNumber(weightKilograms)) {
    const weightAtBmi30 = BMI_OBESITY_THRESHOLD * heightMeters * heightMeters;
    const weightAtBmi25 = BMI_NORMAL_MAX * heightMeters * heightMeters;

    const deltaTo30 = weightKilograms - weightAtBmi30;
    const deltaTo25 = weightKilograms - weightAtBmi25;

    kilogramsToReachBmiBelow30Raw = deltaTo30 > 0 ? deltaTo30 : 0;
    kilogramsToReachNormalUpperRaw = deltaTo25 > 0 ? deltaTo25 : 0;
  }

  // ---- Micronutrient summary for the BMI tile ----
  const nutrientPrioritySummaryText =
    buildNutrientPrioritySummary(comparisonVsTargets);

  return {
    value: isFiniteNumber(bmiValue) ? formatNumber(bmiValue, 1) : null,
    category: bmiClassificationFromSnapshot || null,
    weightKg: isFiniteNumber(weightKilograms)
      ? formatNumber(weightKilograms, 1)
      : null,
    heightCm: isFiniteNumber(heightCentimeters)
      ? formatNumber(heightCentimeters, 1)
      : null,
    heightMeters: heightMeters || null,

    meanLifeStageBmi,
    referenceWeightKg: isFiniteNumber(referenceWeightKg)
      ? formatNumber(referenceWeightKg, 1)
      : null,

    kilogramsAboveReference: isFiniteNumber(kilogramsAboveReferenceRaw)
      ? formatNumber(kilogramsAboveReferenceRaw, 1)
      : null,
    kilogramsToReachBmiBelow30: isFiniteNumber(
      kilogramsToReachBmiBelow30Raw,
    )
      ? formatNumber(kilogramsToReachBmiBelow30Raw, 1)
      : null,
    kilogramsToReachNormalUpper: isFiniteNumber(
      kilogramsToReachNormalUpperRaw,
    )
      ? formatNumber(kilogramsToReachNormalUpperRaw, 1)
      : null,

    energyPercentOfTarget: isFiniteNumber(energyPercentOfTarget)
      ? energyPercentOfTarget
      : null,
    fatPercentOfTarget: isFiniteNumber(fatPercentOfTarget)
      ? fatPercentOfTarget
      : null,
    carbohydratesPercentOfTarget: isFiniteNumber(
      carbohydratesPercentOfTarget,
    )
      ? carbohydratesPercentOfTarget
      : null,
    fibrePercentOfTarget: isFiniteNumber(fibrePercentOfTarget)
      ? fibrePercentOfTarget
      : null,
    proteinPercentOfTarget: isFiniteNumber(proteinPercentOfTarget)
      ? proteinPercentOfTarget
      : null,

    estimatedEnergyRequirementKcal: isFiniteNumber(
      estimatedEnergyRequirementKcalRaw,
    )
      ? Math.round(estimatedEnergyRequirementKcalRaw)
      : null,
    averageIntakeEnergyKcal: isFiniteNumber(averageIntakeEnergyKcalRaw)
      ? Math.round(averageIntakeEnergyKcalRaw)
      : null,
    dailyEnergyBalanceKcal: isFiniteNumber(dailyEnergyBalanceKcalRaw)
      ? Math.round(dailyEnergyBalanceKcalRaw)
      : null,

    // Measured body fat or waist:height if present in the profile.
    bodyFat: userBodyNode?.bodyFat?.percent ?? null,
    waistToHeight: userBodyNode?.bodyFat?.waistToHeight ?? null,

    ageYears: profileNode?.ageYears ?? null,
    sex: profileNode?.sex ?? null,

    // Range meta so the tile can label averages correctly.
    rangeKind,
    rangeDaysInRange,
    rangeTotalDaysLogged,

    // Weight-loss goal meta (used purely for planning in BmiInsight).
    hasWeightLossGoal,
    weightLossGoalKilograms: isFiniteNumber(weightLossGoalKilogramsRaw)
      ? formatNumber(weightLossGoalKilogramsRaw, 1)
      : null,

    // Micronutrient priorities for this BMI tile.
    nutrientPrioritySummaryText:
      typeof nutrientPrioritySummaryText === "string"
        ? nutrientPrioritySummaryText
        : null,
  };
}

export function getFitnessBodyGroups() {
  return [
    {
      name: "Body Comp",
      items: [{ id: "bmi", label: "BMI & Body Fat" }],
    },
  ];
}

/**
 * Build overview “meta” strings shown in the category overview cards.
 */
export function getFitnessBodyOverviewMetas(data = {}) {
  const bmiNode = data.bmi || {};
  const bmiValue = isFiniteNumber(bmiNode.value) ? bmiNode.value : null;
  const category = bmiNode.category || null;

  return [
    {
      id: "body",
      label: "Body Comp",
      meta:
        bmiValue != null
          ? `${formatNumber(bmiValue, 1)}${category ? ` (${category})` : ""
          }`
          : "-",
    },
  ];
}

/**
 * Map each tile to its high-level status used for badges.
 * Here we use BMI ranges instead of just "has data".
 */
export function getFitnessBodyStatuses(data = {}) {
  // Build an insight helper so we reuse all the BMI logic in one place
  const bmiInsight = new BmiInsight(data.bmi || {});
  const bmiValue = bmiInsight.bmiValue;

  // No BMI → we cannot say anything meaningful yet
  if (!isFiniteNumber(bmiValue)) {
    return {
      bmi: "data-needed",
    };
  }

  let bmiStatusKey = "on-track";

  // Obesity range
  if (bmiValue >= BMI_OBESITY_THRESHOLD) {
    bmiStatusKey = "off-track"; // typically mapped to red or strong warning
  } else if (bmiValue >= BMI_OVERWEIGHT_THRESHOLD) {
    // Overweight range
    bmiStatusKey = "at-risk"; // typically mapped to amber/yellow
  } else {
    // Otherwise in the normal (or underweight) band → keep "on-track"
    bmiStatusKey = "on-track";
  }

  return {
    bmi: bmiStatusKey,
  };
}

export function getFitnessBodyFocusEntries(data = {}) {
  return [{ id: "bmi", score: 100 }];
}

/**
 * Convert BMI to a percentage value for the ring visual.
 * This keeps the ring semantics simple: 0–100 uses the raw BMI
 * value (clamped) so 36.4 → 36%.
 */
function bmiToRingPercentage(bmi) {
  if (!isFiniteNumber(bmi)) {
    return 0;
  }
  const clampedBmi = Math.min(100, Math.max(0, bmi));
  return clampedBmi;
}

/**
 * Map BMI status to a ring stroke colour so the hero ring
 * visually matches on-track / at-risk / off-track states.
 */
function bmiRingStrokeForStatus(status) {
  if (status === "off-track") {
    // Obesity range
    return "#ef4444"; // red
  }
  if (status === "at-risk") {
    // Overweight range
    return "#f59e0b"; // amber
  }
  if (status === "on-track") {
    // Normal or underweight
    return "#10b981"; // green
  }
  // Fallback for "data-needed" or unknown
  return "#6b7280"; // grey
}

export function buildFitnessBodySpecialTile(id, node, statuses, label) {
  if (id !== "bmi") {
    return null;
  }

  const safeNode = node || {};
  const bmiInsight = new BmiInsight(safeNode);
  const helperText = bmiInsight.buildHelperText();
  const detailRows = bmiInsight.buildKeyValueRows();
  const bmiValue = bmiInsight.bmiValue;
  const status = statuses?.[id] ?? "data-needed";

  const ringPercentage = bmiToRingPercentage(bmiValue);
  const ringStroke = bmiRingStrokeForStatus(status);

  return {
    status,
    hero: {
      type: "ring",
      pct: ringPercentage,
      mainText: isFiniteNumber(bmiValue)
        ? formatNumber(bmiValue, 1)
        : "-",
      subText: "BMI",
      stroke: ringStroke,
      ariaLabel: isFiniteNumber(bmiValue)
        ? `BMI ${formatNumber(bmiValue, 1)}`
        : "BMI",
    },
    helper: helperText,
    details: {
      type: "kvGrid",
      rows: detailRows,
    },
    detailsAlwaysExpanded: true,
    actions: {
      open: { id, label },
      log: null,
    },
  };
}
